import {
    Component,
    ElementRef,
    OnInit,
    HostListener,
    signal,
    computed,
    viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '@env/environment';
import { FormsModule } from '@angular/forms';
import { DebugService } from '../../services/debug.service';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { categorizeFeatures, DebugFeature, FeatureCategory } from './debug';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

interface ConsolePosition {
    x: number;
    y: number;
}

interface ConsoleSize {
    width: number;
    height: number;
}

type DragMode = 'move' | 'resize';

@UntilDestroy()
@Component({
    selector: 'app-debug-toggle',
    templateUrl: './debug-toggle.component.html',
    styleUrls: ['./debug-toggle.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatSlideToggleModule,
        MatInputModule,
        MatFormFieldModule,
        MatListModule,
        MatMenuModule,
        MatBadgeModule,
        MatTabsModule,
        MatProgressSpinnerModule,
    ],
})
export class DebugToggleComponent implements OnInit {
    dragHandle = viewChild<ElementRef>('dragHandle');

    isDragging = signal(false);
    dragMode = signal<DragMode>('move');
    position = signal<ConsolePosition>({ x: 20, y: 20 });
    size = signal<ConsoleSize>({ width: 320, height: 'auto' as any });
    startPosition = signal({ x: 0, y: 0 });
    startSize = signal({ width: 0, height: 0 });
    isActive = signal(false);
    isExpanded = signal(false);
    isLocked = signal(false);
    searchQuery = signal('');
    activeTab = signal('All');
    selectedTabIndex = signal(0);
    isVisible = signal(true);

    getIsLoading(): boolean {
        return this.debugService.loading();
    }

    private readonly POSITION_KEY = 'debugButtonPosition';
    private readonly SIZE_KEY = 'debugConsoleSize';
    private readonly TAB_KEY = 'debugConsoleTab';
    private readonly LOCKED_KEY = 'debugConsoleLocked';

    currentEnvironment = signal(environment.production ? 'production' : 'test');
    features = signal<DebugFeature[]>([]);
    activeFeatureCount = computed(
        () =>
            this.features().filter(f => f.type === 'toggle' && f.enabled).length
    );

    categoryTabs = computed(() => {
        const categories = new Set(['All']);
        this.features().forEach(feature => {
            if (feature.category) {
                categories.add(feature.category);
            }
        });
        return Array.from(categories);
    });

    filteredFeatures = computed(() => {
        const query = this.searchQuery().toLowerCase().trim();
        if (!query) return this.features();

        return this.features().filter(
            feature =>
                feature.name.toLowerCase().includes(query) ||
                feature.description.toLowerCase().includes(query)
        );
    });

    constructor(private debugService: DebugService) {}

    ngOnInit(): void {
        this.isVisible.set(this.debugService.isDebugVisible());

        if (!this.isVisible() && environment.production) {
            return;
        }

        const savedPosition = localStorage.getItem(this.POSITION_KEY);
        if (savedPosition) {
            try {
                this.position.set(JSON.parse(savedPosition));
            } catch (e) {
                console.error('Error parsing saved position:', e);
            }
        }

        const savedSize = localStorage.getItem(this.SIZE_KEY);
        if (savedSize) {
            try {
                this.size.set(JSON.parse(savedSize));
            } catch (e) {
                console.error('Error parsing saved size:', e);
            }
        }

        const savedTab = localStorage.getItem(this.TAB_KEY);
        if (savedTab) {
            try {
                this.activeTab.set(savedTab);
            } catch (e) {
                console.error('Error parsing saved tab:', e);
            }
        }

        const savedLockedState = localStorage.getItem(this.LOCKED_KEY);
        if (savedLockedState) {
            try {
                this.isLocked.set(savedLockedState === 'true');
            } catch (e) {
                console.error('Error parsing saved locked state:', e);
            }
        }

        this.features.set(this.debugService.getFeatures());

        this.debugService
            .getDebugState()
            .pipe(untilDestroyed(this))
            .subscribe(state => {
                this.features.set(this.debugService.getFeatures());
                this.isActive.set(state.environment === 'test');
                this.currentEnvironment.set(state.environment);
                this.isVisible.set(state.visible === true);

                this.updateSelectedTabIndex();

                if (state.loggingEnabled) {
                    console.log(
                        '[DebugToggle] State updated via subscription:',
                        state
                    );
                }
            });
    }

    hideDebugUI(): void {
        this.debugService.hideDebug();
    }

    updateSelectedTabIndex(): void {
        const tabs = this.categoryTabs();
        const activeTabIndex = tabs.indexOf(this.activeTab());
        if (activeTabIndex !== -1) {
            this.selectedTabIndex.set(activeTabIndex);
        } else {
            this.activeTab.set('All');
            this.selectedTabIndex.set(0);
        }
    }

    onTabChange(index: number): void {
        const tabs = this.categoryTabs();
        if (index >= 0 && index < tabs.length) {
            this.activeTab.set(tabs[index]);
            localStorage.setItem(this.TAB_KEY, tabs[index]);
        }
    }

    clearSearch(): void {
        this.searchQuery.set('');
    }

    getFilteredCategories(tabName?: string): FeatureCategory[] {
        const tab = tabName || this.activeTab();

        if (tab === 'All') {
            return categorizeFeatures(this.features());
        }

        const filteredFeatures = this.features().filter(
            feature => feature.category === tab
        );

        return categorizeFeatures(filteredFeatures);
    }

    onMouseDown(event: MouseEvent, mode: DragMode): void {
        if (mode === 'move') {
            const target = event.target as HTMLElement;
            const dragHandleEl = this.dragHandle()?.nativeElement;
            if (!dragHandleEl?.contains(target)) {
                return;
            }
        }

        this.isDragging.set(true);
        this.dragMode.set(mode);

        if (mode === 'move') {
            this.startPosition.set({
                x: event.clientX - this.position().x,
                y: event.clientY - this.position().y,
            });
        } else if (mode === 'resize') {
            this.startPosition.set({
                x: event.clientX,
                y: event.clientY,
            });
            this.startSize.set({
                width: this.size().width,
                height:
                    typeof this.size().height === 'string'
                        ? 0
                        : this.size().height,
            });
        }

        event.preventDefault();
        event.stopPropagation();
    }
    @HostListener('document:mousemove', ['$event'])
    onMouseMove(event: MouseEvent): void {
        if (!this.isDragging()) return;

        if (this.dragMode() === 'move') {
            this.updatePosition(event);
        } else if (this.dragMode() === 'resize') {
            this.updateSize(event);
        }

        event.preventDefault();
    }

    private updatePosition(event: MouseEvent): void {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        let x = event.clientX - this.startPosition().x;
        let y = event.clientY - this.startPosition().y;

        const consoleWidth = this.size().width || 320;
        const consoleHeight =
            this.dragHandle()?.nativeElement?.closest('.tw-fixed')
                ?.offsetHeight || 40;

        x = Math.max(0, Math.min(x, windowWidth - consoleWidth));
        y = Math.max(0, Math.min(y, windowHeight - consoleHeight));

        this.position.set({ x, y });
    }

    private updateSize(event: MouseEvent): void {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const startWidth = this.startSize().width;
        const deltaX = event.clientX - this.startPosition().x;
        const deltaY = event.clientY - this.startPosition().y;

        const minWidth = 280;
        const maxWidth = Math.min(800, windowWidth - this.position().x - 20);
        const newWidth = Math.max(
            minWidth,
            Math.min(maxWidth, startWidth + deltaX)
        );

        const consoleElement =
            this.dragHandle()?.nativeElement?.closest('.tw-fixed');
        const contentHeight = consoleElement
            ? (
                  consoleElement.querySelector('.debug-console-tabs') ||
                  consoleElement.querySelector('.tw-max-h-\\[320px\\]')
              )?.offsetHeight || 0
            : 0;

        this.size.update(current => ({
            ...current,
            width: newWidth,
            height: this.isExpanded() ? contentHeight + 100 : 'auto',
        }));
    }

    @HostListener('document:mouseup')
    onMouseUp(): void {
        if (this.isDragging()) {
            this.isDragging.set(false);
            localStorage.setItem(
                this.POSITION_KEY,
                JSON.stringify(this.position())
            );
            localStorage.setItem(this.SIZE_KEY, JSON.stringify(this.size()));
        }
    }

    toggleDebugMode(): void {
        const currentEnv = this.currentEnvironment();
        const targetEnv = currentEnv === 'test' ? 'production' : 'test';
        console.log(
            `[DebugToggle] Toggling environment from ${currentEnv} to ${targetEnv}`
        );

        if (targetEnv === 'production') {
            if (
                confirm(
                    'Switching to production mode will hide the debug console. You will need to reload the page to see it again. Continue?'
                )
            ) {
                this.debugService.setEnvironment(targetEnv);
            }
        } else {
            this.debugService.setEnvironment(targetEnv);
        }
    }

    toggleLocked(): void {
        const currentState = this.isLocked();
        this.isLocked.set(!currentState);

        if (!currentState) {
            localStorage.setItem(
                this.POSITION_KEY,
                JSON.stringify(this.position())
            );
        }

        localStorage.setItem(this.LOCKED_KEY, (!currentState).toString());

        if (!currentState) {
            console.log('[DebugToggle] Console locked to top left');
        } else {
            console.log('[DebugToggle] Console unlocked');
        }
    }

    toggleExpanded(): void {
        this.isExpanded.update(value => !value);
    }

    toggleFeature(featureId: string): void {
        const feature = this.features().find(f => f.id === featureId);
        if (feature && feature.type === 'toggle') {
            const newState = !feature.enabled;
            console.log(
                `[DebugToggle] Toggling feature '${featureId}' to ${newState}`
            );
            this.debugService.updateFeature(featureId, newState);
        } else {
            console.warn(
                `[DebugToggle] Attempted to toggle non-toggle feature or feature not found: ${featureId}`
            );
        }
    }

    executeAction(featureId: string): void {
        console.log(`[DebugToggle] Executing action '${featureId}'`);
        this.debugService.executeAction(featureId);
    }

    updateSearchQuery(query: string): void {
        this.searchQuery.set(query);
    }

    getFeatureCategories() {
        return categorizeFeatures(this.filteredFeatures());
    }

    isToggleFeature(feature: DebugFeature): boolean {
        return feature.type === 'toggle';
    }

    isActionFeature(feature: DebugFeature): boolean {
        return feature.type === 'action';
    }

    public trackByCategory(index: number, category: FeatureCategory): string {
        return category.name;
    }

    public trackByFeatureId(index: number, feature: DebugFeature): string {
        return feature.id;
    }
}
